import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { formatCPFOrCNPJ } from 'src/app/helpers/utils/cpf-cnpj.util';
import { CharacteristicConsultService } from 'src/app/pages/register/characteristic-consult/services/characteristic-consult.service';
import { CarbonOperation } from 'src/app/shared/interfaces/carbon-operation';
import { CarbonCredit } from 'src/app/shared/interfaces/carbon-credit';

import {
  isAcxDepositOperation,
  OperationTypeBadge,
  resolveOperationTypeBadge,
} from '../shared/operation-type-badge.utils';

export interface OperationDepositDetailsView {
  issueDate: string;
  comitenteDocumentNumber: string;
  creditInitialQuantity: string;
  creditQuantity: string;
  vintageStart: string;
  vintageEnd: string;
  serialCode: string;
  projectLink: string;
  certifierDescription: string;
  observationText: string;
}

@Component({
  standalone: false,
  selector: 'app-operation-deposit-details-drawer',
  templateUrl: './operation-deposit-details-drawer.component.html',
  styleUrls: ['./operation-deposit-details-drawer.component.scss'],
})
export class OperationDepositDetailsDrawerComponent implements OnChanges {
  @Input() open = false;
  @Input() operation: CarbonOperation | null = null;
  @Output() openChange = new EventEmitter<boolean>();

  details: OperationDepositDetailsView | null = null;
  isLoading = false;
  showBrcerBadge = false;

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['open'] || changes['operation']) && this.open && this.operation) {
      this.loadDetails(this.operation);
    }

    if (changes['open'] && !this.open) {
      this.details = null;
      this.showBrcerBadge = false;
    }
  }

  get operationTypeBadge(): OperationTypeBadge {
    return this.operation ? resolveOperationTypeBadge(this.operation) : {
      label: 'Depósito',
      icon: 'check_circle',
      tone: 'success',
    };
  }

  get showAcxSourceBadge(): boolean {
    return this.operation ? isAcxDepositOperation(this.operation) : false;
  }

  get projectLink(): string {
    return this.details?.projectLink?.trim() ?? '';
  }

  close(): void {
    this.open = false;
    this.openChange.emit(false);
  }

  constructor(
    private readonly characteristicConsultService: CharacteristicConsultService,
  ) {}

  private loadDetails(operation: CarbonOperation): void {
    const carbonCreditCode = operation.carbonCreditCode?.trim();

    if (!carbonCreditCode) {
      this.details = this.createEmptyDetails();
      this.showBrcerBadge = false;
      return;
    }

    this.isLoading = true;

    this.characteristicConsultService.getCarbonCreditById(carbonCreditCode).subscribe({
      next: (carbonCredit: CarbonCredit) => {
        this.details = this.mapCarbonCreditToView(carbonCredit);
        this.showBrcerBadge = this.isB3DepositWithBrcer(carbonCredit);
        this.isLoading = false;
      },
      error: () => {
        this.details = this.createEmptyDetails();
        this.showBrcerBadge = false;
        this.isLoading = false;
      },
    });
  }

  private mapCarbonCreditToView(carbonCredit: CarbonCredit): OperationDepositDetailsView {
    return {
      issueDate: this.displayValue(carbonCredit.issueDate),
      comitenteDocumentNumber: this.formatComitente(carbonCredit.comitenteDocumentNumber),
      creditInitialQuantity: this.displayQuantity(carbonCredit.creditInitialQuantity),
      creditQuantity: this.displayQuantity(carbonCredit.creditQuantity),
      vintageStart: this.displayValue(carbonCredit.vintageStart),
      vintageEnd: this.displayValue(carbonCredit.vintageEnd),
      serialCode: this.displayValue(carbonCredit.serialCode),
      projectLink: this.displayValue(carbonCredit.projectLink),
      certifierDescription: this.displayValue(carbonCredit.certifierDescription),
      observationText: this.displayValue(carbonCredit.observationText),
    };
  }

  private createEmptyDetails(): OperationDepositDetailsView {
    return {
      issueDate: '-',
      comitenteDocumentNumber: '-',
      creditInitialQuantity: '-',
      creditQuantity: '-',
      vintageStart: '-',
      vintageEnd: '-',
      serialCode: '-',
      projectLink: '-',
      certifierDescription: '-',
      observationText: '-',
    };
  }

  private formatComitente(value?: string | null): string {
    if (!value?.trim()) {
      return '-';
    }

    return formatCPFOrCNPJ(value) ?? value;
  }

  private displayQuantity(value?: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }

    return `${value}`;
  }

  private displayValue(value?: string | null): string {
    const trimmed = value?.trim();
    return trimmed ? trimmed : '-';
  }

  private isB3DepositWithBrcer(carbonCredit: CarbonCredit): boolean {
    return (
      !this.showAcxSourceBadge &&
      carbonCredit.brcerIndicator === true
    );
  }
}
